import { menuItems } from './menu-items';
import { MenuItem } from './menu-item';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from '../../../shared/services/auth.service';
import { WalletNavigationService } from '../../../shared/services/wallet-navigation.service';
import { ADMINISTRATOR, AGENCY_CLIENT, CLIENT, PARTNER_CLIENT } from '../../../shared/services/user.service';

/** `/wallet/navigation.hide.*` → caminho do item a esconder no menu (M7, task 17tm466nz8b). */
const LEGACY_WALLET_PATHS: { [key in 'messages_report' | 'lead_responses' | 'recharges']: string } = {
  messages_report: '/messages-report',
  lead_responses: '/lead-responses',
  recharges: '/recharges',
};

/**
 * Preferência do DESKTOP: barra expandida (`true`) ou trilho de ícones (`false`).
 * O celular não grava aqui — ver `setSmallDevice()`.
 */
export const MENU_KEY = 'SMSFUNNEL::MENU';

/** Perfis de cliente: quem compra e usa o painel, em oposição a quem opera. */
const CUSTOMER_PROFILES = [CLIENT, AGENCY_CLIENT, PARTNER_CLIENT];

/**
 * Entrada de back-office: nenhum perfil de cliente a enxerga. Para o
 * administrador ela mora no grupo Admin (`admin-menu-items.ts`), não na
 * navegação principal — que fica com a cara do painel do cliente.
 */
export function isBackOfficeEntry(item: MenuItem): boolean {
  return !item.profiles.some(profile => CUSTOMER_PROFILES.includes(profile));
}

/**
 * Estado da barra lateral (CU-86akkvmxc).
 *
 * São dois estados com regras diferentes, e misturá-los foi o defeito original:
 *
 * - **Desktop** (> 915 px): expandida ou trilho é PREFERÊNCIA do usuário. Ela é
 *   lida do `localStorage` no boot e gravada a cada toque no hambúrguer, então
 *   abrir → recarregar mantém o que ele escolheu. Antes o `MenuComponent`
 *   forçava `show()` na primeira emissão do breakpoint e a chave era só escrita.
 * - **Celular** (≤ 915 px): a barra aberta é uma gaveta por cima do conteúdo,
 *   com overlay. Ela nasce fechada e fecha ao navegar — gaveta que reabre
 *   sozinha a cada recarga cobriria a tela que o usuário acabou de pedir. Por
 *   isso abrir e fechar no celular não toca na preferência do desktop.
 */
@Injectable()
export class MenuService {

  isVisible: boolean;
  showMenu: BehaviorSubject<boolean>;
  isSmallDevice = false;

  /** Preferência do desktop; sobrevive ao boot. */
  private desktopVisible: boolean;

  private isSmallDeviceSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private walletNavigation: WalletNavigationService
  ) {
    this.desktopVisible = this.readSavedVisibility();
    this.isVisible = this.desktopVisible;
    this.showMenu = new BehaviorSubject<boolean>(this.isVisible);
  }

  getMenuItems(): MenuItem[] {
    const user = this.authService.getUser();
    const originalUser = this.authService.getUser(true);
    const isAdmin = originalUser.profile_id === ADMINISTRATOR;

    const hasAnyReport = user.analytics_sync
      || (user.analytics_sales_sync && user.show_sales_report)
      || (user.funnel_report_enabled && user.show_funnel_report);

    // Mensagens/Respostas/Recarga somem para o cliente MIGRADO (M7, task 17tm466nz8b):
    // relatório e extrato da carteira substituem as três telas, canal por canal. Quem
    // decide é `GET /wallet/navigation` (`WalletNavigationService`) — o front não deduz
    // migração a partir de flag do `user`, que o próprio contrato avisa ficar desatualizada
    // depois que o admin migra o cliente. Diferente do escape de admin dos outros itens:
    // aqui o admin só escapa FORA de simulação — simulando um cliente migrado, ele vê o menu
    // igual ao cliente (decisão do Bruno, 14/10), por isso `user.simulated` entra na condição.
    const walletHide = this.walletNavigation.current.hide;
    const isLegacyWalletHidden = (path: string): boolean => {
      const hideKey = (Object.keys(LEGACY_WALLET_PATHS) as Array<keyof typeof LEGACY_WALLET_PATHS>)
        .find(key => LEGACY_WALLET_PATHS[key] === path);
      return !!hideKey && walletHide[hideKey];
    };

    const passes = (item: MenuItem): boolean => {
      if (!item.profiles.includes(user.profile_id)) { return false; }
      if (user.simulated && !item.simulated) { return false; }
      if (user.globalActivated && !item.global) { return false; }
      if (item.requiresAi && !user.ai_enabled) { return false; }
      if (item.requiresAiOrWhatsapp && !user.ai_enabled && !user.whatsapp_enabled) { return false; }
      // RCS (17tm466frtx): o grupo do canal só existe para quem tem `rcs_enabled` na conta.
      if (item.requiresRcs && !user.rcs_enabled) { return false; }
      // Escape de admin, igual ao da linha de analytics logo abaixo e pelo
      // mesmo motivo: `integration_sms_enabled` nasce false para TODA a base,
      // contas de admin inclusive. Sem ele, o ADMINISTRATOR declarado em
      // menu-items.ts seria decorativo e ninguém veria o item até alguém ligar
      // a flag na própria conta de admin. `isAdmin` vem do usuário ORIGINAL,
      // então numa simulação o admin continua vendo a tela do cliente que atende.
      if (item.requiresIntegrationSms && !isAdmin && !user.integration_sms_enabled) { return false; }
      if (!isAdmin && !hasAnyReport && item.path.includes('analytics')) { return false; }
      if ((!isAdmin || user.simulated) && isLegacyWalletHidden(item.path)) { return false; }
      return true;
    };

    const authorized = menuItems
      .filter(passes)
      .map(item => item.children ? { ...item, children: item.children.filter(passes) } : item);

    // "Deixa tudo dentro do Admin" (Bruno, 28/09/2026): para o administrador a
    // navegação principal é a do cliente, e o back-office inteiro vive no grupo
    // Admin. Só muda o ENDEREÇO no menu — as rotas e quem pode abri-las ficam
    // como estão. O Customer Success não tem grupo Admin, então para ele nada
    // sai daqui.
    const withoutBackOffice = user.profile_id === ADMINISTRATOR
      ? authorized
        .filter(item => !isBackOfficeEntry(item))
        .map(item => item.children
          ? { ...item, children: item.children.filter(child => !isBackOfficeEntry(child)) }
          : item)
      : authorized;

    // Remove grupos que ficaram sem nenhum filho visível
    return withoutBackOffice.filter(item => !item.children || item.children.length > 0);
  }

  getMenuVisibility(): Observable<boolean> {
    return this.showMenu.asObservable();
  }

  getIsSmallDevice(): Observable<boolean> {
    return this.isSmallDeviceSubject.asObservable();
  }

  hide(): void {
    this.setVisible(false);
  }

  show(): void {
    this.setVisible(true);
  }

  toggle(): void {
    this.setVisible(!this.isVisible);
  }

  /**
   * Chamado pelo breakpoint, inclusive na primeira emissão do boot. Não decide
   * nada por conta própria no desktop: devolve a preferência salva. No celular,
   * fecha a gaveta — sem gravar, para não apagar a preferência do desktop.
   */
  setSmallDevice(isSmallDevice: boolean): void {
    this.isSmallDevice = isSmallDevice;
    this.isSmallDeviceSubject.next(isSmallDevice);
    this.emit(isSmallDevice ? false : this.desktopVisible);
  }

  private setVisible(isVisible: boolean): void {
    if (!this.isSmallDevice) {
      this.desktopVisible = isVisible;
      this.saveVisibility();
    }
    this.emit(isVisible);
  }

  private emit(isVisible: boolean): void {
    this.isVisible = isVisible;
    if (this.showMenu.value !== isVisible) {
      this.showMenu.next(isVisible);
    }
  }

  /** Sem valor salvo (ou valor ilegível), a barra nasce expandida. */
  private readSavedVisibility(): boolean {
    try {
      const saved = JSON.parse(localStorage.getItem(MENU_KEY));
      return typeof saved === 'boolean' ? saved : true;
    } catch {
      return true;
    }
  }

  private saveVisibility(): void {
    try {
      localStorage.setItem(MENU_KEY, JSON.stringify(this.desktopVisible));
    } catch {
      // Sem storage (aba privada, cota): a preferência vale só nesta sessão.
    }
  }

}
